import * as qs from 'querystring';

import React, { useState, useEffect, useCallback } from 'react';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';

import * as api from '../../api';
import * as logger from '../../logger';
import * as rootReducer from '../../reducers/root';
import * as store from '../../reducers/store';

import Loader from '../../components/Loader';

import { PreAuthLoginRouteParams } from '../router';
import { sendEventMessage, Message } from '../../utils/native';
import { getFormStartPath } from '../../utils/form';
import { useIsAuthenticated } from '../../hooks/authentication/useIsAuthenticated';

///////////////////////////////////////////////////
//////INTERFACES

interface DispatchProps {
  setLoggedInPatient: (loggedInPatient: store.Patient) => void;
}

interface Props
  extends DispatchProps,
    RouteComponentProps<PreAuthLoginRouteParams> {}

///////////////////////////////////////////////////
////// COMPONENT

const PreAuthLoginComponent: React.FC<Props> = ({
  match,
  setLoggedInPatient,
  location,
}) => {
  const { authenticationCode, authBypassCode } = match.params;

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [redirectToFormStart, setRedirectToFormStart] = useState(false);
  const { isAuthenticated, isPending } = useIsAuthenticated(authenticationCode);

  const login = useCallback(async () => {
    const { patient } = await api.client.Previsit.preAuthLogin(
      authenticationCode,
      authBypassCode,
    );
    if (patient) {
      setLoggedInPatient(patient);
    }
  }, [authenticationCode, authBypassCode, setLoggedInPatient]);

  useEffect(() => {
    const fetchData = async () => {
      const queryValues = qs.parse(location.search.slice(1));
      const source = queryValues && `${queryValues.source}`;

      try {
        await api.client.Previsit.createPrevisitLog({
          auditLog: api.Log.PrevisitAuditAction.PatientPrevisitPreAuthLogin,
          previsitLog: api.Log.PrevisitAction.NavigatedToPreAuthSignInPage,
          authenticationCode,
          source,
        });

        if (!isAuthenticated) {
          await login();
        }

        setRedirectToFormStart(true);
      } catch (err) {
        logger.error(new Error(`PreAuthLogin: Failed to login`));
        sendEventMessage(Message.Logout);
        setError(true);
        return;
      } finally {
        setIsLoading(false);
      }
    };
    if (!isPending) fetchData();
  }, [location.search, login, authenticationCode, isAuthenticated, isPending]);

  const { from: formLocation } = {
    from: getFormStartPath({
      authenticationCode,
      multi: false,
      allowReview: true,
    }),
  };
  if (isLoading) return <Loader />;

  if (error) {
    return (
      <Redirect
        to={{ pathname: `/login/${match.params.authenticationCode}` }}
      />
    );
  }
  if (redirectToFormStart) return <Redirect to={formLocation} />;

  return null;
};

function mapDispatchToProps(dispatch: Function): DispatchProps {
  return {
    setLoggedInPatient: (patient: store.Patient) =>
      dispatch(rootReducer.setLoggedInPatient(patient)),
  };
}

export default connect(undefined, mapDispatchToProps)(PreAuthLoginComponent);
